import React from 'react';
import { useAtomValue } from 'jotai';
import { Box, CircularProgress } from '@mui/material';
import { FlashMessage } from 'c-components';
import { atom_useLegacySummary } from 'c-wrapper/Atoms/legacyViewAtoms';
import CampaignAnalyticsES from './CampaignAnalyticsES';
import CampaignAnalyticsESMain from './CampaignAnalyticsES.main';
import SummarySwitcher from './SummarySwitcher';
import { useApplySummarySource, useSummaryIsDsp, useSummarySources } from './useSummarySources';

// Picks which Summary renders. The Summary 2.0 toggle decides first: with it off, the
// pre-customisation (main branch) Summary renders as it always has, with no switcher, because
// an assigned summary is a saved Summary 2.0 layout and can't be shown there. With it on, the
// switcher centred under the Summary tab chooses between the built-in summary and any assigned.
const CampaignAnalyticsToggle: React.FC<
    React.ComponentProps<typeof CampaignAnalyticsES>
> = props => {
    const { id } = props;
    const legacy = useAtomValue(atom_useLegacySummary);
    const isDsp = useSummaryIsDsp(id);
    const { sources, active, activeKey, activeConfig, isLoading, hasError, select } =
        useSummarySources(isDsp, !legacy);
    // The legacy Summary reads the same widget atoms, so with 2.0 off the built-in summary is
    // what gets applied — which also clears an assigned layout left there from when it was on.
    const shown = legacy ? sources[0] : active;
    const isApplied = useApplySummarySource(id, isDsp, shown, legacy ? null : activeConfig);

    if (legacy) return isApplied ? <CampaignAnalyticsESMain {...props} /> : null;

    const switcher = (
        <SummarySwitcher
            sources={sources}
            activeKey={activeKey}
            activeName={active.name}
            onSelect={select}
        />
    );

    // While the Summary itself isn't rendered, the switcher still is, so a viewer is never stuck
    // on a summary that won't load.
    const placeholder = (content: React.ReactNode) => (
        <Box sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>{switcher}</Box>
            {content}
        </Box>
    );
    const spinner = placeholder(
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
        </Box>,
    );

    if (active.kind === 'assigned') {
        if (isLoading || (!hasError && !isApplied)) return spinner;
        if (hasError) {
            return placeholder(
                <Box sx={{ p: 2 }}>
                    <FlashMessage status="error">
                        The summary &quot;{active.name}&quot; could not be loaded. Choose another
                        from the switcher above.
                    </FlashMessage>
                </Box>,
            );
        }
        return <CampaignAnalyticsES {...props} centerControls={switcher} />;
    }

    if (!isApplied) return spinner;
    return <CampaignAnalyticsES {...props} centerControls={switcher} />;
};

export default CampaignAnalyticsToggle;
